import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import {
  completeStockTransfer,
  createStockTransfer,
  getStockTransferById,
  getStockTransfers,
} from "../../apis/stockTransfers";
import { getStockLocations } from "../../apis/stockLocations";
import { getPharmacyItems, getPharmacyItemBatches } from "../../apis/pharmacyItems";
import { useNotification } from "../../NotificationContext";
import CommonPagination from "../../components/CommanPagination";

const { Option } = Select;
const { TextArea } = Input;

const toArray = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

interface TransferLine {
  key: string;
  pharmacyItemId?: number;
  batchId?: number;
  quantity: number;
  batches: any[];
  loadingBatches?: boolean;
}

const StockTransfersList: React.FC = () => {
  const { showNotification } = useNotification();
  const [rows, setRows] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>();
  const [loading, setLoading] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [pharmacyItems, setPharmacyItems] = useState<any[]>([]);
  const [fromLocationId, setFromLocationId] = useState<number | null>(null);
  const [toLocationId, setToLocationId] = useState<number | null>(null);
  const [status, setStatus] = useState<"pending" | "completed">("completed");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<TransferLine[]>([
    { key: `line-${Date.now()}`, quantity: 1, batches: [] },
  ]);

  const [selected, setSelected] = useState<any | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [completingId, setCompletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchRows({ page: 1, per_page: 10 });
  }, []);

  const fetchRows = (params: Record<string, any> = {}) => {
    setLoading(true);
    getStockTransfers({ per_page: 10, ...params })
      .then((res: any) => {
        const data = res.data?.data || res.data || [];
        setRows(Array.isArray(data) ? data : []);
        setMeta(res.meta || res.data?.meta || {});
      })
      .catch(() => showNotification("error", "Failed to fetch stock transfers", "", "topRight"))
      .finally(() => setLoading(false));
  };

  const openCreateModal = async () => {
    setIsCreateOpen(true);
    setStatus("completed");
    setFromLocationId(null);
    setToLocationId(null);
    setNotes("");
    setLines([{ key: `line-${Date.now()}`, quantity: 1, batches: [] }]);

    try {
      const [locRes, itemRes]: any[] = await Promise.all([
        getStockLocations({ is_active: true }),
        getPharmacyItems({ per_page: 200, is_active: true }),
      ]);
      const locs = toArray(locRes);
      setLocations(locs);
      setPharmacyItems(toArray(itemRes));

      const defaultLoc = locs.find((l: any) => l.isDefault || l.is_default);
      if (defaultLoc) setFromLocationId(defaultLoc.id);
    } catch {
      message.error("Failed to load form options");
    }
  };

  const handleItemChange = async (key: string, pharmacyItemId: number) => {
    setLines((prev) =>
      prev.map((line) =>
        line.key === key
          ? {
              ...line,
              pharmacyItemId,
              batchId: undefined,
              batches: [],
              loadingBatches: true,
            }
          : line
      )
    );
    try {
      const res: any = await getPharmacyItemBatches(pharmacyItemId);
      const batches = res?.batches || res?.data?.batches || res?.data || [];
      setLines((prev) =>
        prev.map((line) =>
          line.key === key
            ? { ...line, batches: Array.isArray(batches) ? batches : [], loadingBatches: false }
            : line
        )
      );
    } catch {
      message.error("Failed to load batches");
      setLines((prev) =>
        prev.map((line) => (line.key === key ? { ...line, loadingBatches: false } : line))
      );
    }
  };

  const handleBatchChange = (key: string, batchId: number) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, batchId } : line))
    );
  };

  const handleCreate = async () => {
    if (!fromLocationId || !toLocationId) {
      message.warning("Select from and to locations");
      return;
    }
    if (fromLocationId === toLocationId) {
      message.warning("From and to locations must be different");
      return;
    }
    const validLines = lines.filter(
      (line) => line.pharmacyItemId && line.batchId && line.quantity > 0
    );
    if (validLines.length === 0) {
      message.warning("Add at least one item with batch and quantity");
      return;
    }

    setCreating(true);
    try {
      await createStockTransfer({
        from_location_id: fromLocationId,
        to_location_id: toLocationId,
        status,
        notes: notes || null,
        items: validLines.map((line) => ({
          pharmacy_item_id: line.pharmacyItemId,
          batch_id: line.batchId,
          quantity: line.quantity,
        })),
      });
      message.success(
        status === "completed"
          ? "Transfer completed — stock moved between locations"
          : "Transfer saved as pending"
      );
      setIsCreateOpen(false);
      fetchRows({ page: meta?.current_page || 1, per_page: meta?.per_page || 10 });
    } catch (err: any) {
      message.error(
        err?.response?.data?.details?.message ||
          err?.response?.data?.message ||
          "Failed to create transfer"
      );
    } finally {
      setCreating(false);
    }
  };

  const handleView = (id: number) => {
    getStockTransferById(id)
      .then((res: any) => {
        setSelected(res.data?.data || res.data || res);
        setIsViewOpen(true);
      })
      .catch(() => message.error("Failed to fetch transfer details"));
  };

  const handleComplete = async (id: number) => {
    setCompletingId(id);
    try {
      await completeStockTransfer(id);
      message.success("Transfer completed");
      fetchRows({ page: meta?.current_page || 1, per_page: meta?.per_page || 10 });
      if (selected?.id === id) {
        const res: any = await getStockTransferById(id);
        setSelected(res.data?.data || res.data || res);
      }
    } catch (err: any) {
      message.error(
        err?.response?.data?.details?.message ||
          err?.response?.data?.message ||
          "Failed to complete transfer"
      );
    } finally {
      setCompletingId(null);
    }
  };

  const locName = (loc: any) => loc?.name || "-";

  const batchQty = (batch: any) =>
    batch.currentQuantity ??
    batch.current_quantity ??
    batch.availableQuantity ??
    batch.available_quantity ??
    "-";

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Stock Transfers</h1>
          <p className="mt-1 text-sm text-gray-500">
            Move pharmacy stock between locations (e.g. Main Pharmacy → ICU / Housekeeping). No
            patient or sale. Transfer to a terminal location is the consumption event.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="mt-4 sm:mt-0 px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          New Transfer
        </button>
      </div>

      <div className="flow-root mt-8">
        <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  Transfer
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">From</th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">To</th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Items</th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    No transfers found
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                      {row.transferNumber || `#${row.id}`}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-700">
                      {locName(row.fromLocation)}
                      {row.fromLocation?.type === "terminal" ? (
                        <Tag className="ml-1" color="orange">
                          terminal
                        </Tag>
                      ) : null}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-700">
                      {locName(row.toLocation)}
                      {row.toLocation?.type === "terminal" ? (
                        <Tag className="ml-1" color="orange">
                          terminal
                        </Tag>
                      ) : null}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-700">
                      {row.itemsCount ?? 0} / qty {row.totalQuantity ?? 0}
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <Tag color={row.status === "completed" ? "green" : "orange"}>
                        {(row.status || "").toUpperCase()}
                      </Tag>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-700">
                      {row.transferDate
                        ? format(new Date(row.transferDate), "dd MMM yyyy, h:mm a")
                        : "-"}
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <div className="flex gap-2">
                        <Button size="small" onClick={() => handleView(row.id)}>
                          View
                        </Button>
                        {row.status === "pending" ? (
                          <Button
                            size="small"
                            type="primary"
                            loading={completingId === row.id}
                            onClick={() => handleComplete(row.id)}
                          >
                            Complete
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <CommonPagination
          meta={meta}
          onChange={(page, pageSize) => fetchRows({ page, per_page: pageSize || 10 })}
        />
      </div>

      <Modal
        title="New Stock Transfer"
        open={isCreateOpen}
        onCancel={() => setIsCreateOpen(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setIsCreateOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={creating} onClick={handleCreate}>
            {status === "completed" ? "Transfer Now" : "Save Pending"}
          </Button>,
        ]}
      >
        <Form layout="vertical" className="mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Form.Item label="From Location" required>
              <Select
                showSearch
                optionFilterProp="children"
                value={fromLocationId ?? undefined}
                onChange={setFromLocationId}
                placeholder="Select source"
              >
                {locations.map((loc) => (
                  <Option key={loc.id} value={loc.id} disabled={loc.type === "terminal"}>
                    {loc.name} ({loc.type})
                    {loc.isDefault || loc.is_default ? " ★" : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="To Location" required>
              <Select
                showSearch
                optionFilterProp="children"
                value={toLocationId ?? undefined}
                onChange={setToLocationId}
                placeholder="Select destination"
              >
                {locations.map((loc) => (
                  <Option key={loc.id} value={loc.id} disabled={loc.id === fromLocationId}>
                    {loc.name} ({loc.type})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Status">
              <Select value={status} onChange={(v) => setStatus(v)}>
                <Option value="completed">Completed (move stock now)</Option>
                <Option value="pending">Pending</Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item label="Notes">
            <TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Form.Item>

          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">Items to transfer</h3>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setLines((prev) => [
                  ...prev,
                  { key: `line-${Date.now()}`, quantity: 1, batches: [] },
                ])
              }
            >
              Add Item
            </Button>
          </div>

          <div className="space-y-3 max-h-80 overflow-auto">
            {lines.map((line) => (
              <div
                key={line.key}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border rounded-md p-3 bg-gray-50"
              >
                <div className="md:col-span-5">
                  <label className="text-xs text-gray-600">Item</label>
                  <Select
                    showSearch
                    className="w-full"
                    optionFilterProp="children"
                    placeholder="Select item"
                    value={line.pharmacyItemId}
                    onChange={(v) => handleItemChange(line.key, v)}
                  >
                    {pharmacyItems.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.name}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-xs text-gray-600">Batch</label>
                  <Select
                    className="w-full"
                    loading={line.loadingBatches}
                    disabled={!line.pharmacyItemId}
                    placeholder={line.loadingBatches ? "Loading..." : "Select batch"}
                    value={line.batchId}
                    onChange={(v) => handleBatchChange(line.key, v)}
                  >
                    {line.batches.map((batch) => (
                      <Option key={batch.id} value={batch.id}>
                        {batch.batchNumber || batch.batch_number} · qty {batchQty(batch)}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-xs text-gray-600">Quantity</label>
                  <InputNumber
                    className="w-full"
                    min={1}
                    value={line.quantity}
                    onChange={(v) =>
                      setLines((prev) =>
                        prev.map((l) =>
                          l.key === line.key ? { ...l, quantity: Number(v) || 1 } : l
                        )
                      )
                    }
                  />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={lines.length === 1}
                    onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                  />
                </div>
              </div>
            ))}
          </div>
        </Form>
      </Modal>

      <Modal
        title="Transfer Details"
        open={isViewOpen}
        onCancel={() => {
          setIsViewOpen(false);
          setSelected(null);
        }}
        width={800}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsViewOpen(false);
              setSelected(null);
            }}
          >
            Close
          </Button>,
          selected?.status === "pending" ? (
            <Button
              key="complete"
              type="primary"
              loading={completingId === selected?.id}
              onClick={() => handleComplete(selected.id)}
            >
              Complete Transfer
            </Button>
          ) : null,
        ]}
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-semibold text-gray-700">Number</p>
                <p>{selected.transferNumber}</p>
              </div>
              <div>
                <p className="font-semibold text-gray-700">Status</p>
                <Tag color={selected.status === "completed" ? "green" : "orange"}>
                  {(selected.status || "").toUpperCase()}
                </Tag>
              </div>
              <div>
                <p className="font-semibold text-gray-700">From</p>
                <p>
                  {selected.fromLocation?.name} ({selected.fromLocation?.type})
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-700">To</p>
                <p>
                  {selected.toLocation?.name} ({selected.toLocation?.type})
                </p>
              </div>
            </div>
            <table className="min-w-full text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-left">Batch</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(selected.items || []).map((item: any) => (
                  <tr key={item.id}>
                    <td className="px-3 py-2">{item.pharmacyItem?.name}</td>
                    <td className="px-3 py-2">{item.batch?.batchNumber || "-"}</td>
                    <td className="px-3 py-2 text-right">{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StockTransfersList;
